import type { ClearableSiteField, Site, SiteAddress, SiteContactPatch } from './inventory';

// Matches the backend's per-field limit in UpdateSite.
export const SITE_CONTACT_MAX_LENGTH = 200;

/**
 * A site's address being edited. The site's contact name and phone moved
 * to Contacts (tidaltech/koi#8359): the site's Receiving and Operations
 * contacts.
 */
export interface SiteContactDraft {
  line1: string;
  line2: string;
  postcode: string;
  city: string;
}

export type SiteContactDraftField = keyof SiteContactDraft;

export const SITE_CONTACT_FIELD_LABELS: Record<SiteContactDraftField, string> = {
  line1: 'Address line 1',
  line2: 'Address line 2',
  postcode: 'Postcode',
  city: 'City',
};

const ADDRESS_FIELDS = ['line1', 'line2', 'postcode', 'city'] as const;

function trimmed(value: string | undefined): string {
  return value?.trim() ?? '';
}

/**
 * Tidies a phone number the way Leatherback stores it: trimmed, with runs
 * of spaces collapsed.
 */
export function tidyPhone(phone: string): string {
  return phone.trim().split(/\s+/).filter(Boolean).join(' ');
}

/**
 * Whether Leatherback accepts the phone number: digits, spaces, "-", "("
 * and ")" with an optional leading "+", and 6 to 15 digits. "" is allowed.
 */
export function isValidPhone(phone: string): boolean {
  const tidy = tidyPhone(phone);
  if (!tidy) return true;
  const digits = tidy.replace(/\D/g, '').length;
  return /^\+?[0-9 ()-]+$/.test(tidy) && digits >= 6 && digits <= 15;
}

export const PHONE_HINT = 'Use digits and spaces, e.g. +47 900 00 000.';

export function hasSiteAddress(address: SiteAddress | undefined): boolean {
  return ADDRESS_FIELDS.some((field) => trimmed(address?.[field]) !== '');
}

// "Line 1, Line 2, 1234 City" — skips whatever parts are missing.
export function formatSiteAddress(address: SiteAddress | undefined): string | undefined {
  if (!hasSiteAddress(address)) return undefined;
  const locality = [trimmed(address?.postcode), trimmed(address?.city)].filter(Boolean).join(' ');
  return [trimmed(address?.line1), trimmed(address?.line2), locality].filter(Boolean).join(', ');
}

export function siteContactSearchText(site: Pick<Site, 'address'>): string {
  return formatSiteAddress(site.address) ?? '';
}

export function siteContactDraft(site: Pick<Site, 'address'>): SiteContactDraft {
  return {
    line1: site.address?.line1 ?? '',
    line2: site.address?.line2 ?? '',
    postcode: site.address?.postcode ?? '',
    city: site.address?.city ?? '',
  };
}

export function validateSiteContactDraft(
  draft: SiteContactDraft,
): Partial<Record<SiteContactDraftField, string>> {
  const errors: Partial<Record<SiteContactDraftField, string>> = {};
  for (const field of Object.keys(draft) as SiteContactDraftField[]) {
    if (trimmed(draft[field]).length > SITE_CONTACT_MAX_LENGTH) {
      errors[field] =
        `${SITE_CONTACT_FIELD_LABELS[field]} must be at most ${SITE_CONTACT_MAX_LENGTH} characters.`;
    }
  }
  const addressDraft = {
    line1: draft.line1,
    line2: draft.line2,
    postcode: draft.postcode,
    city: draft.city,
  };
  if (hasSiteAddress(addressDraft)) {
    if (!trimmed(draft.postcode)) errors.postcode ??= 'Postcode is required for an address.';
    if (!trimmed(draft.city)) errors.city ??= 'City is required for an address.';
  }
  return errors;
}

function addressFromDraft(draft: SiteContactDraft): SiteAddress | undefined {
  const address: SiteAddress = {};
  for (const field of ADDRESS_FIELDS) {
    const value = trimmed(draft[field]);
    if (value) address[field] = value;
  }
  return hasSiteAddress(address) ? address : undefined;
}

function sameAddress(left: SiteAddress | undefined, right: SiteAddress | undefined): boolean {
  return ADDRESS_FIELDS.every((field) => trimmed(left?.[field]) === trimmed(right?.[field]));
}

/**
 * Builds the UpdateSite patch for an address edit, or null when nothing
 * changed. Emptying the address becomes an explicit clear, and a changed
 * address is always sent whole (the backend replaces it as a unit).
 */
export function buildSiteContactPatch(
  site: Site,
  version: number,
  draft: SiteContactDraft,
): SiteContactPatch | null {
  const patch: SiteContactPatch = { id: site.id, customerId: site.customerId, version };
  const clear: ClearableSiteField[] = [];

  const address = addressFromDraft(draft);
  if (!sameAddress(address, site.address)) {
    if (address) patch.address = address;
    else clear.push('address');
  }

  if (clear.length > 0) patch.clear = clear;
  return patch.address !== undefined || clear.length > 0 ? patch : null;
}
